import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Upload, Mail, Settings, Gift, Plus, CircleDollarSign } from 'lucide-react';

interface SidebarProps {
  currentPath: string;
  isAdmin?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ currentPath, isAdmin }) => {
  const navItems = [
    { path: '/listings', icon: Home, label: 'Listings' },
    { path: '/upload', icon: Upload, label: 'Upload' },
    { path: '/pricing', icon: CircleDollarSign, label: 'Pricing' },
    { path: '/referrals', icon: Gift, label: 'Referrals' },
    ...(isAdmin ? [{ path: '/admin/processing', icon: Settings, label: 'Admin' }] : []),
  ];

  const bottomItems = [
    { path: '/contact', icon: Mail, label: 'Contact' },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <aside className="relative z-10 w-16 lg:w-64 bg-slate-950/70 backdrop-blur-xl border-r border-slate-800/80 flex flex-col shrink-0">
      {/* Logo */}
      <div className="h-28 flex items-center justify-center lg:justify-start px-3 lg:px-5 border-b border-slate-800/80">
        <Link to="/listings" className="flex items-center group">
          <img
            src="/hdr-photo-logo.png"
            alt="HDR Photo"
            className="h-[2.7890625rem] lg:h-[3.984375rem] w-auto object-contain group-hover:opacity-90 transition-opacity"
          />
        </Link>
      </div>

      {/* New Listing CTA */}
      <div className="px-2 lg:px-3 pt-4">
        <Link
          to="/upload"
          className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-medium text-sm shadow-lg shadow-brand-900/30 transition-colors"
        >
          <Plus size={18} className="shrink-0" />
          <span className="hidden lg:block">New Listing</span>
        </Link>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 py-4">
        <ul className="space-y-1 px-2 lg:px-3">
          {navItems.map((item) => {
            const isActive = item.path.startsWith('/admin')
              ? currentPath.startsWith('/admin')
              : currentPath.startsWith(item.path);
            const Icon = item.icon;

            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                    isActive
                      ? 'bg-brand-500/10 text-brand-300'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/70'
                  }`}
                >
                  {/* Active accent bar */}
                  <span
                    className={`absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-r-full bg-brand-400 transition-opacity ${
                      isActive ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                  <Icon size={20} className="shrink-0" />
                  <span className="hidden lg:block font-medium">{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Bottom Navigation */}
      <div className="py-4 border-t border-slate-800/80">
        <ul className="space-y-1 px-2 lg:px-3">
          {bottomItems.map((item) => {
            const Icon = item.icon;

            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800/70 transition-colors"
                >
                  <Icon size={20} className="shrink-0" />
                  <span className="hidden lg:block font-medium">{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </aside>
  );
};

export default Sidebar;
